/**
 * Centralized query key factory for IIMReview feature
 *
 * This ensures consistency across all React Query hooks and prevents typos.
 * Each factory function returns a properly typed query key array.
 *
 * Usage:
 * - For queries: queryKey: iimReviewKeys.issues(inspectionId, statuses)
 * - For invalidation: queryClient.invalidateQueries({ queryKey: iimReviewKeys.issues(inspectionId) })
 * - For partial invalidation: queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) })
 */
export const iimReviewKeys = {
    /**
     * Base key for all IIM review queries
     */
    all: ['iim-review'],
    /**
     * Base key for all IIM issues queries for a specific inspection
     * Use this for invalidating ALL issues queries regardless of status filters
     * @param inspectionId - The inspection ID
     */
    issuesAll: (inspectionId) => [...iimReviewKeys.all, 'issues', inspectionId],
    /**
     * Key for IIM issues with optional status filters
     * @param inspectionId - The inspection ID
     * @param statuses - Optional array of issue statuses to filter by
     */
    issues: (inspectionId, statuses) => [...iimReviewKeys.issuesAll(inspectionId), statuses === null || statuses === void 0 ? void 0 : statuses.join('-')],
    /**
     * Base key for all IIM issues-grid queries for a specific inspection.
     * Use this for optimistic updates/invalidation across all grid filter combos.
     * @param inspectionId - The inspection ID
     */
    issuesGridAll: (inspectionId) => [...iimReviewKeys.all, 'issues-grid', inspectionId],
    /**
     * Key for IIM issues grid (v2) with filter/sort params
     * @param inspectionId - The inspection ID
     * @param params - Filter and sort parameters serialized as string
     */
    issuesGrid: (inspectionId, params) => [...iimReviewKeys.all, 'issues-grid', inspectionId, params],
    /**
     * Key for IIM runs with activities
     * @param inspectionId - The inspection ID
     */
    runs: (inspectionId) => [...iimReviewKeys.all, inspectionId],
    inspectionAssessment: (inspectionId) => [...iimReviewKeys.all, 'ipa-inspection-assessment', inspectionId],
    techAssessments: (inspectionId) => [...iimReviewKeys.all, 'ipa-tech-assessments', inspectionId],
    techAssessmentDetail: (inspectionId, technicianId) => [...iimReviewKeys.techAssessments(inspectionId), technicianId],
    /**
     * Key for the issue bucket/type counts shown in the AI summary banner
     * @param inspectionId - The inspection ID
     */
    bucketCounts: (inspectionId) => [...iimReviewKeys.all, 'bucket-counts', inspectionId],
    /**
     * Key holding the current user's (optimistic) thumbs vote for an inspection assessment
     * @param assessmentId - The inspection assessment ID
     */
    assessmentRating: (assessmentId) => [...iimReviewKeys.all, 'assessment-rating', assessmentId]
};
